import React, { useState } from 'react';
import { UsersIcon, PlusIcon, EditIcon, UserCheckIcon, UserXIcon, ShieldIcon, UserIcon, AlertCircleIcon, Trash2Icon, LockIcon, SearchIcon } from 'lucide-react';
import { Card, CardHeader } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Badge } from '../components/ui/Badge';
import { StatsCard } from '../components/ui/StatsCard';
import { PageHeader } from '../components/ui/PageHeader';
import { Modal, ModalBody, ModalFooter } from '../components/ui/Modal';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { User } from '../types';
export function UsersPage() {
  const {
    users,
    roles,
    addUser,
    updateUser,
    deleteUser
  } = useApp();
  const {
    addActivityLog
  } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    password: '',
    rolId: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const resetForm = () => {
    setFormData({
      nombre: '',
      email: '',
      password: '',
      rolId: roles.length > 0 ? roles[0].id : ''
    });
    setErrors({});
    setEditingUser(null);
    setSubmitError('');
  };
  const openCreateModal = () => {
    resetForm();
    setIsModalOpen(true);
  };
  const openEditModal = (user: User) => {
    setEditingUser(user);
    setFormData({
      nombre: user.nombre,
      email: user.email,
      password: '',
      rolId: user.rolId
    });
    setSubmitError('');
    setErrors({});
    setIsModalOpen(true);
  };
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.nombre.trim()) newErrors.nombre = 'El nombre es requerido';
    if (!formData.rolId) newErrors.rolId = 'El rol es requerido';
    if (!editingUser && !formData.password) newErrors.password = 'La contraseña es requerida para nuevos usuarios';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = async () => {
    if (!validateForm()) return;
    setIsSubmitting(true);
    setSubmitError('');
    try {
      if (editingUser) {
        const updates: Partial<User> = {
          nombre: formData.nombre,
          email: formData.email,
          rolId: formData.rolId
        };
        if (formData.password) updates.password = formData.password;
        const result = await updateUser(editingUser.id, updates);
        if (!result.success) throw new Error(result.error);
        addActivityLog('Editó usuario', `Usuario: ${formData.nombre} | Rol: ${roles.find(r => r.id === formData.rolId)?.nombre || ''}`);
      } else {
        const result = await addUser({
          nombre: formData.nombre,
          email: formData.email,
          password: formData.password,
          rolId: formData.rolId,
          activo: true
        });
        if (!result.success) throw new Error(result.error);
        addActivityLog('Creó usuario', `Usuario: ${formData.nombre} | Rol: ${roles.find(r => r.id === formData.rolId)?.nombre || ''}`);
      }
      setIsModalOpen(false);
      resetForm();
    } catch (err: unknown) {
      setSubmitError(err instanceof Error ? err.message : 'Error al guardar usuario');
    } finally {
      setIsSubmitting(false);
    }
  };
  const toggleUserStatus = async (user: User) => {
    await updateUser(user.id, {
      activo: !user.activo
    });
    addActivityLog(user.activo ? 'Desactivó usuario' : 'Activó usuario', `Usuario: ${user.nombre}`);
  };
  const handleDelete = async (user: User) => {
    if (!confirm(`¿Eliminar a ${user.nombre}? Esta acción no se puede deshacer.`)) return;
    await deleteUser(user.id);
    addActivityLog('Eliminó usuario', `Usuario: ${user.nombre}`);
  };
  const roleOptions = roles.map(r => ({
    value: r.id,
    label: r.nombre
  }));
  const activeUsers = users.filter(u => u.activo);
  const inactiveUsers = users.filter(u => !u.activo);
  const filteredUsers = users.filter(u => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return u.nombre.toLowerCase().includes(q) || u.email.toLowerCase().includes(q) || u.rolNombre.toLowerCase().includes(q);
  });
  const getUserAvatar = (userId: string): string => {
    try {
      return localStorage.getItem(`ventastrack_avatar_${userId}`) || '';
    } catch {
      return '';
    }
  };
  const getInitials = (name: string) => name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
  return <div className="space-y-6" data-id="element-3611">
      <PageHeader title="Gestión de Usuarios" subtitle="Administración de vendedores y personal" icon={<UsersIcon className="w-6 h-6" data-id="element-3613" />} data-id="element-3612">
        <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-3615" />} onClick={openCreateModal} data-id="element-3614">
          Nuevo Usuario
        </Button>
      </PageHeader>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 animate-stagger-1" data-id="element-3616">
        <StatsCard title="Total Usuarios" value={users.length} icon={<UsersIcon className="w-5 h-5" data-id="element-3618" />} variant="primary" data-id="element-3617" />
        <StatsCard title="Usuarios Activos" value={activeUsers.length} icon={<UserCheckIcon className="w-5 h-5" data-id="element-3620" />} variant="success" data-id="element-3619" />
        <StatsCard title="Usuarios Inactivos" value={inactiveUsers.length} icon={<UserXIcon className="w-5 h-5" data-id="element-3622" />} variant="danger" data-id="element-3621" />
      </div>

      {/* Search */}
      <div className="relative max-w-md animate-stagger-2" data-id="element-3623">
        <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-3624" />
        <Input placeholder="Buscar usuarios..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10" data-id="element-3625" />
      </div>

      {/* Table */}
      <Card padding="none" className="animate-stagger-3" data-id="element-3626">
        <div className="px-6 py-5 border-b border-slate-100" data-id="element-3627">
          <CardHeader title="Lista de Usuarios" data-id="element-3628" />
        </div>
        <div className="divide-y divide-slate-100" data-id="element-3629">
          {filteredUsers.length === 0 ? <div className="p-16 text-center animate-fade-in" data-id="element-3630">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-100 rounded-2xl mb-4" data-id="element-3631">
                <UsersIcon className="w-8 h-8 text-slate-400" data-id="element-3632" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-1" data-id="element-3633">
                No hay usuarios
              </h3>
              <p className="text-slate-500 text-sm" data-id="element-3634">
                Crea el primer usuario con el botón de arriba
              </p>
            </div> : filteredUsers.map(user => <div key={user.id} className="px-6 py-4 hover:bg-slate-50/50 transition-colors" data-id="element-3635">
                <div className="flex items-center justify-between" data-id="element-3636">
                  <div className="flex items-center gap-4" data-id="element-3637">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center overflow-hidden flex-shrink-0" style={{
                backgroundColor: user.avatarUrl ? 'transparent' : '#eef2fc'
              }} data-id="element-3638">
                      {user.avatarUrl ? <img src={user.avatarUrl} alt={user.nombre} className="w-full h-full object-cover" data-id="element-3639" /> : <span className="text-sm font-bold" style={{
                  color: '#2952C4'
                }} data-id="element-3640">
                          {getInitials(user.nombre)}
                        </span>}
                    </div>
                    <div data-id="element-3641">
                      <div className="flex items-center gap-2 flex-wrap" data-id="element-3642">
                        <h4 className="font-semibold text-slate-900 font-heading" data-id="element-3643">
                          {user.nombre}
                        </h4>
                        <Badge variant={user.activo ? 'success' : 'danger'} data-id="element-3644">
                          {user.activo ? 'Activo' : 'Inactivo'}
                        </Badge>
                        <Badge variant={user.rolNombre === 'Administrador' ? 'info' : 'default'} data-id="element-3645">
                          {user.rolNombre}
                        </Badge>
                      </div>
                      <p className="text-xs text-slate-400 mt-0.5" data-id="element-3646">
                        {user.email} • Creado:{' '}
                        {new Date(user.fechaCreacion).toLocaleDateString('es-DO')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-3647">
                    <Button variant="ghost" size="sm" onClick={() => openEditModal(user)} data-id="element-3648">
                      <EditIcon className="w-4 h-4" data-id="element-3649" />
                    </Button>
                    <Button variant={user.activo ? 'danger' : 'success'} size="sm" onClick={() => toggleUserStatus(user)} data-id="element-3650">
                      {user.activo ? 'Desactivar' : 'Activar'}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(user)} data-id="element-3651">
                      <Trash2Icon className="w-4 h-4 text-rose-400" data-id="element-3652" />
                    </Button>
                  </div>
                </div>
              </div>)}
        </div>
      </Card>

      {/* Modal */}
      <Modal isOpen={isModalOpen} onClose={() => {
      setIsModalOpen(false);
      resetForm();
    }} title={editingUser ? 'Editar Usuario' : 'Nuevo Usuario'} data-id="element-3653">
        <ModalBody data-id="element-3654">
          <div className="space-y-4" data-id="element-3655">
            {submitError && <div className="flex items-start gap-3 p-3 bg-rose-50 border border-rose-200 rounded-xl" data-id="element-3656">
                <AlertCircleIcon className="w-5 h-5 text-rose-500 flex-shrink-0 mt-0.5" data-id="element-3657" />
                <p className="text-sm text-rose-700" data-id="element-3658">{submitError}</p>
              </div>}
            <Input label="Nombre Completo" placeholder="Ej. Juan Pérez" value={formData.nombre} onChange={e => setFormData({
            ...formData,
            nombre: e.target.value
          })} error={errors.nombre} data-id="element-3659" />
            <Input label="Email (para iniciar sesión)" placeholder="juan@empresa.com" type="email" value={formData.email} onChange={e => setFormData({
            ...formData,
            email: e.target.value
          })} data-id="element-3660" />
            <Select label="Rol" options={roleOptions} value={formData.rolId} onChange={e => setFormData({
            ...formData,
            rolId: e.target.value
          })} error={errors.rolId} data-id="element-3661" />
            <Input label={editingUser ? 'Nueva Contraseña (dejar en blanco para mantener)' : 'Contraseña'} type="password" placeholder="••••••••" value={formData.password} onChange={e => setFormData({
            ...formData,
            password: e.target.value
          })} error={errors.password} icon={<LockIcon className="w-4 h-4 text-slate-400" data-id="element-3663" />} data-id="element-3662" />
          </div>
        </ModalBody>
        <ModalFooter data-id="element-3664">
          <Button variant="secondary" onClick={() => {
          setIsModalOpen(false);
          resetForm();
        }} data-id="element-3665">
            Cancelar
          </Button>
          <Button onClick={handleSubmit} isLoading={isSubmitting} data-id="element-3666">
            {editingUser ? 'Guardar Cambios' : 'Crear Usuario'}
          </Button>
        </ModalFooter>
      </Modal>
    </div>;
}